<script>
    import { createEventDispatcher, tick } from "svelte";
    import PlayerManagement from "./PlayerManagement.svelte";
    import ProgressBanner from "./ProgressBanner.svelte";
    import { addPlayersSequentialV2, genBonusIdV2 } from "../../../utils/freebetsIgp.js";
    import { CAMPAIGN_STATUS, computeCampaignStatus } from "../../../utils/statusEngine.js";
    import {ICONS} from "../../../../js/icons.js";
    import { onMount } from "svelte"
    import { get } from "svelte/store";

    // ====================== Props (API) =====================
    export let campaign = null;
    export let actions = {
        addPlayer: async (_campaignId, _payload) => { throw new Error("addPlayer no inyectado"); },
        removePlayer: async (_campaignId, _playerId) => { throw new Error("removePlayer no inyectado"); },
    };
    export let getCampaignByCampaignId = async (_campaignId) => null;
    export let demoConfig = null;
    export let autoRefetchOnOpen = true;
    export let idStrategy = 'auto';
    export let showExtraActions = false;
    export let adapter;
    // ====================== Local variables =====================
    const dispatch = createEventDispatcher();
    let busy = false;
    let progress = null;     // { done, total } | null
    let showManagePlayers = false;
    let refreshing = false;
    let players = [];

    // ====================== Reactive =====================
    $: failedPlayers = players.filter(p => Number(p.playerStatus) === FAILED_STATUS);
    $: titleName = (campaign?.campaignName || "Campaña").toString();
    $: titleId   = (campaign?.campaignId   || "").toString();
    $: header    = titleId ? `${titleName} (${titleId})` : titleName;
    // $: if (campaign?.playerList) {
    //         players = [...campaign.playerList];
    //     } else {
    //         players = [];
    //     }

    $: players = Array.isArray(campaign?.playerList) ? campaign.playerList : [];
    $: assignedIds = players.map(p => String(p.playerId)).filter(Boolean);
    $: tablePlayers = players.map((p, i) => ({
        ...p,
        _row: i + 1,
        _info: statusInfo(p.playerStatus),
        _open: p._open || false  // para expandir/cerrar
    }));

    // ===== Demo helpers =====
    function demoPlayers(cfg){ return Array.isArray(cfg?.PLAYERS) ? cfg.PLAYERS : []; }
    function demoCurrency(cfg){ return cfg?.DEFAULTS?.CURRENCY || ''; }
    $: demoClientCode = String(campaign?.client?.clientCode || "");
    $: demoClientId   = String(campaign?.client?.clientId   || "");
    $: isDemo = !!demoConfig && (
        demoClientCode === String(demoConfig?.CODE || "") ||
        demoClientId   === String(demoConfig?.ID   || "")
    );
    $: staticPlayersList = (isDemo && demoPlayers(demoConfig).length)
        ? demoPlayers(demoConfig).map(p => ({
            value: String(p.id),
            label: p.label || String(p.id),
            currency: String(p.currency || campaign?.currency || demoCurrency(demoConfig) || "")
        }))
        : null;

    // ====================== Const variables =====================   
    const FAILED_STATUS = -1;
    const nextFrame = () => new Promise(requestAnimationFrame);
    const STATUS_KEY = { 0:"DELETED", 1:"ACTIVE", 2:"COMPLETED", 3:"EXPIRED", 4:"SCHEDULED", "-1":"FAILED" };
    const STATUS_META = {
        DELETED: {label: "Eliminado", class: "bg-secondary text-white"},
        ACTIVE: {label: "Activo", class: "bg-primary text-white"},
        COMPLETED: {label: "Completado", class: "bg-success text-white"},
        EXPIRED: {label: "Expirado", class: "bg-danger text-white"},
        SCHEDULED: {label: "Programado", class: "bg-warning text-dark"},
        FAILED:{label: "Fallido", class: "bg-dark text-white"}
    };
    function statusInfo(code){
        const key = STATUS_KEY[Number(code) ?? 0] || "DELETED";
        return STATUS_META[key] || STATUS_META.DELETED;
    }
    function mergeUniquePlayers(baseList, toAdd) {
        const seen = new Set((baseList || []).map(p => String(p.playerId)));
        const merged = [...(baseList || [])];
        for (const it of (toAdd || [])) {
            const id = String(it.playerId);
            if (!seen.has(id)) { merged.push(it); seen.add(id); }
        }
        return merged;
    }
    function updatePlayersOnParent(list){
        dispatch("playersUpdated", {
            campaignId: campaign?.campaignId || "",
            playerList: Array.isArray(list) ? list : []
        });
    }
    function getInitialPlayerStatus() {
        return Number(effectiveCampaignStatus) === CAMPAIGN_STATUS.ACTIVE
            ? CAMPAIGN_STATUS.ACTIVE   // 1
            : CAMPAIGN_STATUS.SCHEDULED; // 4 (default)
    }
    $: startMs = campaign?.startDate ? new Date(campaign.startDate).getTime() : 0;
    $: endMs   = campaign?.endDate   ? new Date(campaign.endDate).getTime()   : 0;
    $: baseCampaignStatus = Number(
        campaign?.baseStatus ?? campaign?.campaignStatus ?? campaign?.statusCode ?? CAMPAIGN_STATUS.SCHEDULED
           );
    $: effectiveCampaignStatus = computeCampaignStatus(baseCampaignStatus, startMs, endMs);
    function campaignIsClosed(){
          const s = Number(effectiveCampaignStatus);
          return s === CAMPAIGN_STATUS.DELETED || s === CAMPAIGN_STATUS.FINISHED;
    }
    async function retryFailed() {
        if (!failedPlayers.length) return;
        busy = true;
        const items = failedPlayers.map(p => ({
            playerId: p.playerId,
            playerName: p.playerName || null,
            baseBonusCode: p.baseBonusCode ?? null,
            playerStatus: getInitialPlayerStatus()
        }));
        try {
            await addPlayersToCampaign(items);
        } catch(err) {
            console.error("retryFailed error", err);
            alert("No se pudo reintentar los jugadores fallidos.");
        }
        busy = false;
    }
    // ====================== Actions =====================
    async function addPlayersToCampaign(items){
        if (!campaign?.campaignId || !items?.length) return;
        busy = true;
        progress = { done: 0, total: items.length, errors: 0 };
        await tick(); await nextFrame();
        try {
            await addPlayersSequentialV2({
                campaignId: campaign.campaignId,
                players: items,
                actions,
                genBonus: () => genBonusIdV2(adapter?.bonusPrefix || "", 12),
                onStage: stage => console.log("STAGE:", stage),
                onTick: ({ done, total, errors }) => {
                    progress = { done, total, errors };
                }

            });
            campaign.playerList = mergeUniquePlayers(
                campaign.playerList,
                items
            );
            await refetchOnOpen();
            updatePlayersOnParent(campaign.playerList);
        } catch (err) {
            console.error(err);
            alert("No se pudo agregar jugadores a la campaña.");
        } finally {
            busy = false;
            const p = progress;
            if (p && p.total > 0 && p.done >= p.total) {
                setTimeout(() => (progress = null), 700);
            } else {
                progress = null;
            }
        }
    }

    async function removePlayerFromCampaign(p, promo){
        if (!campaign?.campaignId) return;
        busy = true;
        try {
            console.log("campaignREMOVE", campaign);
            console.log("promoREMOVE", promo);
            const result = await actions.removePlayer(campaign.campaignId, p, promo);
            if (!result.ok) {
                dispatch("errorRemovingPlayer", {
                    message: result.error
                });
                return;
            }
            campaign = {
                ...campaign,
                playerList: (campaign.playerList || []).map(x => {
                    if (String(x.playerId) !== String(p.playerId)) return x;
                    return {
                        ...x,
                        deliveredPromos: (x.deliveredPromos || []).filter(
                            d => d.bonusCode !== promo.bonusCode
                        )
                    };
                })
            };
            
            updatePlayersOnParent(campaign.playerList || []);
        } catch (err) {
            console.error(err);
            alert("No se pudo eliminar el jugador de la campaña.");
        } finally {
            busy = false;
        }
    }

    async function refetchOnOpen(){
        const id = campaign?.campaignId;
        if (!id || typeof getCampaignByCampaignId !== 'function') return;
        refreshing = true;
        try {
            const res = await getCampaignByCampaignId(id);
            const raw = res?.data ?? res;
            const fresh = Array.isArray(raw) ? raw[0] : raw;
            if (fresh) {
                const keep = { bonusPrefix: campaign?.bonusPrefix };
                campaign = { ...campaign, ...fresh, ...keep };
                dispatch("campaignRefreshed", { campaignId: id, doc: fresh });
            }
        } catch(_) {
        } finally {
            refreshing = false;
        }
    }

    // ======= Handlers =======
    function toggleRow(player) {
        const willOpen = !player._open;
        tablePlayers = tablePlayers.map(p => ({
            ...p,
            _open: false
        }));
        if (willOpen) {
            tablePlayers = tablePlayers.map(p =>
                p.playerId === player.playerId 
                    ? { ...p, _open: true }
                    : p
            );
        }
    }

    function onAddPlayersClicked(){ showManagePlayers = true; }
    function onConfirmPlayers(e){
        const items = e.detail?.players || [];
        showManagePlayers = false;
        if (!items.length) return;
        addPlayersToCampaign(items);
    }
    function onRemove(p, promo){
        if (!confirm(`¿Eliminar jugador ${p.playerName || p.playerId}?`)) return;
        removePlayerFromCampaign(p, promo);
    }
    // funcion viewmore pasar campaind
    async function ApiCampaingIdAndBonusCode(p, promo){
        try {
            const res = await adapter.temporalAPI({
                 params: {
                    campaignId: campaign?.campaignId || "",
                    bonusCode: promo?.bonusCode || ""
                }
            });
            const data = res?.data ?? res;
            console.log("data", data);
            alert(`Información adicional para ${p.playerName || p.playerId} - ${promo.bonusCode}:\n\n${JSON.stringify(data, null, 2)}`);
        } catch (err) {
            console.error("Error fetching additional info", err);   
        }
    };

    function close(){ dispatch("close"); }

    onMount(async () => {
        console.log("savedFailed1");

        await tick();
        await nextFrame();  

        if (!campaign?.campaignId) {
            console.log("⛔ campaignId no disponible en onMount");
            return;
        }
        if (autoRefetchOnOpen) {
            await refetchOnOpen();

            const store = get(failedPlayersStore);
            const savedFailed = store[campaign.campaignId] || [];

            if (savedFailed.length > 0) {
                campaign = {
                    ...campaign,
                    playerList: mergeUniquePlayers(campaign.playerList, savedFailed)
                };
                updatePlayersOnParent(campaign.playerList);
            }
        }
    });


    function onView(player) {
        dispatch("viewPlayer", player);
    }

    // function onEdit(player) {
    //     dispatch("editPlayer", player);
    // }
</script>

<div class="modal-backdrop fade show"></div>
<div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
    <div class="modal-dialog modal-lg modal-dialog-centered fixed-modal">
        <div class="modal-content modal-fixed">
            <!-- HEADER -->
            <div class="modal-header">
                <h5 class="modal-title">{header}</h5>
                <button type="button" class="btn-close" on:click={close} disabled={busy}></button>
            </div>

            <!-- BODY -->
            <div class="modal-body modal-body-fixed">
                <div class="top-actions d-flex justify-content-between align-items-center mb-2">
                    <div class="small text-muted">
                        Cliente: {campaign?.client?.clientName || '—'} ·
                        Moneda: {campaign?.currency || '—'} ·
                        Tipo: {campaign?.campaignType || '—'}
                    </div>
                    <button class="btn btn-warning btn-sm"
                            on:click={retryFailed}
                            disabled={busy || !failedPlayers.length}>
                        🔁 Reintentar fallidos 
                    </button>

                    {#if !showExtraActions}
                        <button class="btn btn-primary btn-sm"
                                on:click={onAddPlayersClicked}
                                disabled={busy || !campaign?.client?.clientId || !campaign?.currency || campaignIsClosed()}>
                            {@html (ICONS.addCircle)} Agregar jugadores
                        </button>
                    {/if}
                </div>
                <div class="body-scroll">
                    <div class="scroll-head">
                        {#if progress}
                            <ProgressBanner
                                    sticky={false}
                                    stage={(progress.done >= progress.total) ? 'done' : 'adding'}
                                    total={progress.total}
                                    done={progress.done}
                                    errors={progress.errors || 0}
                            />
                        {/if}
                    </div>
                    {#if !players.length}
                        <div class="empty">No hay jugadores en esta campaña aún.</div>
                    {:else}
                        <div class="table-responsive">
                            <table class="table table-striped table-hover table-bordered align-middle">
                                <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Jugador ID</th>
                                    <th>Jugador</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {#each tablePlayers as p}
                                    
                                    <tr on:click={() => toggleRow(p)} class:p-open={p._open} >
                                        <td>{p._row}</td>
                                        <td>{p.playerId}</td>
                                        <td>{p.playerName || '—'}</td>
                                    
                                        <td>
                                            <span class={`badge ${p._info.class}`}>{p._info.label}</span>
                                        </td>
                                    
                                        <td>
                                        </td>
                                    </tr>
                                    {#if p.deliveredPromos && p.deliveredPromos.length > 0}
                                    {#if p._open}
                                        <tr class="">
                                            <td colspan="5">
                                                <div class="p-2">                 
                                                    {#each p.deliveredPromos as promo}
                                                        <div class="d-flex justify-content-between align-items-center border rounded p-2 my-1">
                                                            <div>
                                                                <strong>{promo.gameId}</strong> · {promo.bonusCode} . Estado {STATUS_KEY[Number(promo.status) ?? 0] || "DELETED"}
                                                            </div>
                                                            
                                                            <button class="btn btn-danger btn-sm"
                                                                disabled={promo.status == 0} on:click={onRemove(p, promo)}
                                                                >
                                                                {promo.status == 0 ? "Eliminado" : "Eliminar"}
                                                            </button>
                                                            <!-- <button
                                                                on:click={ApiCampaingIdAndBonusCode(p, promo)}
                                                            >
                                                                ver
                                                            </button> -->
                                                        </div>
                                                    {/each}
                                                    
                                                </div>
                                            </td>
                                        </tr>
                                    {/if}
                                    {/if}
                                {/each}
                                </tbody>
                            </table>
                        </div>
                    {/if}
                </div>
            </div>

            <!-- FOOTER -->
            <div class="modal-footer sticky-footer">
            <button class="btn btn-secondary" on:click={close} disabled={busy}>Cerrar</button>
            </div>
        </div>
    </div>
</div>

{#if showManagePlayers}
    <div class="nested-dim" aria-hidden="true"></div>
    <PlayerManagement
            clientId={campaign?.client?.clientId}
            currency={campaign?.currency}
            selectionMode="multiple"
            initialSelected={[]}
            idStrategy={idStrategy}
            excludeIds={assignedIds}
            staticPlayers={staticPlayersList}
            on:close={() => (showManagePlayers = false)}
            on:confirm={onConfirmPlayers}
    />
{/if}

<style>
    
    .fixed-modal{ max-width: 900px; }
    .modal-fixed{ height: 80vh; display: flex; flex-direction: column; }
    .modal-body-fixed{ flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
    .body-scroll{ position:relative; flex:1 1 auto; min-height:0; overflow:auto; }
    .scroll-head{ position: sticky; top: 0; z-index: 3; background: #fff; padding-top: .25rem; padding-bottom: .25rem; }
    .modal-footer.sticky-footer{ flex: 0 0 auto; position: sticky; bottom: 0; background: #fff; z-index: 1; border-top: 1px solid var(--bs-border-color, #e5e7eb); margin-top: 0; }
    .table-responsive{ max-height: unset; overflow: visible; }
    .empty{ flex: 1; display: grid; place-items: center; padding: 1rem; color: #777; border: 1px dashed #ddd; border-radius: 8px;}
    tr.p-open {
        background-color: #f2faff !important;  /* un azul suave */
        border-left: 4px solid #0d6efd; /* borde azul bootstrap */
        transition: all 0.2s ease;
    }
    tr.p-open td {
        font-weight: 600;
    }
    tr:not(.p-open):hover {
        background-color: #f8f9fa;
    }

</style>
